import log from 'loglevel';

//eslint-disable-next-line no-restricted-imports
import type DxfArrayScanner from '../../DxfArrayScanner';
//eslint-disable-next-line no-restricted-imports
import { serializeGroupValue } from '../../DxfArrayScanner';
//eslint-disable-next-line no-restricted-imports
import type { GroupValue } from '../../DxfArrayScanner';
//eslint-disable-next-line no-restricted-imports
import {
  groupIs,
  parsePoint,
  logUnhandledGroup,
  serializePoint,
} from '../../ParseHelpers';
//eslint-disable-next-line no-restricted-imports
import { DXFSymbol } from '../../constants';
//eslint-disable-next-line no-restricted-imports
import type { IPoint } from '../../entities/geometry';
import {
  parseApplicationGroup,
  serializeApplicationDefinedGroups,
} from '../tables';
import type { ApplicationDefinedGroups } from '../tables';

export type IViewPort = {
  name: string;
  handle?: string;
  lowerLeftCorner?: IPoint;
  upperRightCorner?: IPoint;
  center?: IPoint;
  snapBasePoint?: IPoint;
  snapSpacing?: IPoint;
  gridSpacing?: IPoint;
  viewDirectionFromTarget?: IPoint;
  viewTarget?: IPoint;
  height?: number;
  width?: number;
  lensLength?: number;
  frontClippingPlane?: string | number | boolean;
  backClippingPlane?: string | number | boolean;
  viewHeight?: number;
  snapRotationAngle?: number;
  viewTwistAngle?: number;
  orthographicType?: string;
  ucsOrigin?: IPoint;
  ucsXAxis?: IPoint;
  ucsYAxis?: IPoint;
  renderMode?: string;
  defaultLightingType?: boolean;
  defaultLightingOn?: string;
  ownerHandle?: string;
  ambientColorIndex?: number;
  ambientColor?: number;
  ambientColorGroupCodes?: number[]; //421 or 431 or both
  standardFlags?: number;
  viewMode?: number;
  circleSides?: number;
  plotable?: number;
  UCSIcon?: number;
  elevation?: number;
  visualStyleHardPointer?: number;
  majorGridLines?: number;
  brightness?: number;
  contrast?: number;
  applicationGroups?: ApplicationDefinedGroups;
  sunId?: string;

  //Note: subMarkers insertion order must be maintained when serializing
  //subMarkers are necessary for eDrawings to import dxf we serialize
  //vports use 1 or 2 subMarker types. 3rd and 4th are added for insurance
  subMarkerCount?: number;
  subMarker1?: string;
  subMarker2?: string;
  subMarker3?: string;
  subMarker4?: string;

  //undocumented in autocad 2012 dxf reference, but exists. Required for serialization
  groupCode60?: number;
  groupCode65?: number;
  groupCode75?: number;
  groupCode76?: number;
  groupCode77?: number;
  groupCode78?: number;
};

const viewPortPropertyFromCode = new Map<number, [keyof IViewPort, boolean]>([
  [2, ['name', false]],
  [5, ['handle', false]],
  [10, ['lowerLeftCorner', true]],
  [11, ['upperRightCorner', true]],
  [12, ['center', true]],
  [13, ['snapBasePoint', true]],
  [14, ['snapSpacing', true]],
  [15, ['gridSpacing', true]],
  [16, ['viewDirectionFromTarget', true]],
  [17, ['viewTarget', true]],
  [40, ['height', false]],
  [41, ['width', false]],
  [42, ['lensLength', false]],
  [43, ['frontClippingPlane', false]],
  [44, ['backClippingPlane', false]],
  [45, ['viewHeight', false]],
  [50, ['snapRotationAngle', false]],
  [51, ['viewTwistAngle', false]],
  [61, ['majorGridLines', false]],
  [70, ['standardFlags', false]],
  [71, ['viewMode', false]],
  [72, ['circleSides', false]],
  [73, ['plotable', false]],
  [74, ['UCSIcon', false]],
  [79, ['orthographicType', false]],
  [110, ['ucsOrigin', true]],
  [111, ['ucsXAxis', true]],
  [112, ['ucsYAxis', true]],
  [141, ['brightness', false]],
  [142, ['contrast', false]],
  [146, ['elevation', false]],
  [281, ['renderMode', false]],
  [292, ['defaultLightingOn', false]],
  [330, ['ownerHandle', false]],
  [348, ['visualStyleHardPointer', false]],
  [361, ['sunId', false]],

  [63, ['ambientColorIndex', false]],
  [282, ['defaultLightingType', false]],

  [60, ['groupCode60', false]],
  [65, ['groupCode65', false]],
  [75, ['groupCode75', false]],
  [76, ['groupCode76', false]],
  [77, ['groupCode77', false]],
  [78, ['groupCode78', false]],
]);

const codeFromViewPortProperty = new Map<keyof IViewPort, number>(
  Array.from(viewPortPropertyFromCode.entries()).map(([code, [property]]) => [
    property,
    code,
  ]),
);

//delete special cases that are are handled differently when serializing and parsing
viewPortPropertyFromCode.delete(282);

codeFromViewPortProperty.set('subMarker1', 100);
codeFromViewPortProperty.set('subMarker2', 100);
codeFromViewPortProperty.set('subMarker3', 100);
codeFromViewPortProperty.set('subMarker4', 100);

export const parseViewPortRecords = (scanner: DxfArrayScanner): IViewPort[] => {
  const viewPorts: IViewPort[] = []; // Multiple table entries may have the same name indicating a multiple viewport configuration
  let viewPort = {} as IViewPort;

  log.debug('ViewPort {');
  let curr = scanner.next<string | number | boolean>();
  while (!groupIs(curr, 0, DXFSymbol.EndTable)) {
    const propertyAndIsPoint = viewPortPropertyFromCode.get(curr.code);
    if (propertyAndIsPoint != null) {
      const [property, isPoint] = propertyAndIsPoint;
      if (isPoint) {
        (viewPort[property] as IPoint) = parsePoint(scanner);
      } else {
        (viewPort[property] as GroupValue) = curr.value;
      }
      curr = scanner.next();
    } else {
      //Special cases of setting properties
      switch (curr.code) {
        case 0:
          // New ViewPort
          if (curr.value === DXFSymbol.VPort) {
            log.debug('}');
            viewPorts.push(viewPort);
            log.debug('ViewPort {');
            viewPort = {} as IViewPort;
          }
          curr = scanner.next();
          break;
        case 100:
          if (!viewPort.subMarkerCount) {
            viewPort.subMarkerCount = 1;
          } else {
            viewPort.subMarkerCount++;
          }
          viewPort[`subMarker${viewPort.subMarkerCount}` as 'subMarker1'] =
            curr.value as string;
          curr = scanner.next();
          break;
        case 102:
          if (
            typeof curr.value === 'string' &&
            curr.value.startsWith('{') &&
            curr.value.length > 0
          ) {
            const name = curr.value.substring(1);
            if (!viewPort.applicationGroups) viewPort.applicationGroups = {};
            viewPort.applicationGroups[name] = parseApplicationGroup(scanner);
          }
          curr = scanner.next();
          break;
        case 421:
          if (!viewPort.ambientColorGroupCodes) {
            viewPort.ambientColorGroupCodes = [];
          }
          viewPort.ambientColorGroupCodes.push(curr.code);
          viewPort.ambientColor = curr.value as number;
          curr = scanner.next();
          break;
        case 431:
          if (!viewPort.ambientColorGroupCodes) {
            viewPort.ambientColorGroupCodes = [];
          }
          viewPort.ambientColorGroupCodes.push(curr.code);
          viewPort.ambientColor = curr.value as number;
          curr = scanner.next();
          break;
        case 282:
          viewPort.defaultLightingType = curr.value === 1;
          curr = scanner.next();
          break;
        default:
          logUnhandledGroup(curr);
          curr = scanner.next();
      }
    }
  }
  // Note: do not call scanner.next() here,
  //  parseTable() needs the current group
  log.debug('}');
  viewPorts.push(viewPort);

  return viewPorts;
};

export const serializeViewPorts = function* (
  viewPorts: IViewPort[],
): IterableIterator<string> {
  for (const viewPort of viewPorts) {
    yield '0';
    yield DXFSymbol.VPort;
    for (const [property, value] of Object.entries(viewPort)) {
      const code = codeFromViewPortProperty.get(property as keyof IViewPort);
      if (code != null) {
        if (typeof value === 'object') {
          yield* serializePoint(value as IPoint, code);
        } else {
          yield* serializeGroupValue(code, value);
        }
      } else {
        switch (property) {
          case 'ambientColor':
            for (const c of viewPort.ambientColorGroupCodes ?? [431]) {
              yield `${c}`;
              yield `${value}`;
            }
            break;
          case 'applicationGroups':
            yield* serializeApplicationDefinedGroups(
              value as ApplicationDefinedGroups,
            );
            break;
        }
      }
    }
  }
};
